import {
  BulkItemUpsertRequest,
  DimensionUnit,
  ItemPropertyToUpsert,
  Packaging,
  SerialNumberCaptureMode,
  WeightUnit
} from '../../shared/services/ItemMasterService';
import {InventoryConfiguration, InventoryInfo} from './ItemMasterInterfaces';
import {ItemMasterEditProps, SkuDetailProperties} from './ItemMasterEdit';
import {UpdatedItemDetails} from './ItemMasterDetail';
import {getFromUnitType} from './UomHelpers';

export const fetchSkuDetailProperties = (props: ItemMasterEditProps): SkuDetailProperties => {
  return {
    isHazmat: props.hazmat_viewdata?.is_hazmat,
    limitedQuantity: props.hazmat_viewdata?.is_ormd,
    unNumber: props.hazmat_classification?.un_number,
    pkgCode: props.hazmat_classification?.pkg_code,
    lotTrackingEnabled: props.inventory_configuration?.lot_code_tracking_enabled,
    expiryDateTrackingEnabled: props.inventory_configuration?.expiry_date_tracking_enabled,
    bagAndTapeRequired: props.inventory_configuration?.bag_and_tape_required
  };
};

export const mapToUpdatedItemDetails = (
  inventoryInfo: InventoryInfo,
  inventoryConfig: InventoryConfiguration,
  skuDetailProperties: SkuDetailProperties
): UpdatedItemDetails => {
  return {
    updatedInventoryInfo: inventoryInfo,
    updatedInventoryConfiguration: {
      ...inventoryConfig,
      lot_code_tracking_enabled: skuDetailProperties.lotTrackingEnabled,
      expiry_date_tracking_enabled: skuDetailProperties.expiryDateTrackingEnabled
    },
    updatedHazmatViewdata: {
      is_hazmat: skuDetailProperties.isHazmat,
      is_ormd: skuDetailProperties.limitedQuantity
    },
    updatedHazmatClassification: {
      un_number: skuDetailProperties.unNumber,
      pkg_code: skuDetailProperties.pkgCode
    }
  };
};

const mapToItemPropertyToUpsert = (inventoryInfo: InventoryInfo): ItemPropertyToUpsert[] => {
  return inventoryInfo.properties.map((property) => {
    return {
      packaging: property.packaging as Packaging,
      parentPackaging: (property.parent as Packaging) || null,
      barcodeType: property.barcode_type,
      barcode: property.barcode,
      shipAsIs: property.ship_as_is,
      shipAlone: property.ship_alone,
      unitsPerParent: property.units_per_parent,
      dimensionUnit: (getFromUnitType(property.dimension_unit) as DimensionUnit) || null,
      length: property.length || null,
      width: property.width || null,
      height: property.height || null,
      weightUnit: (getFromUnitType(property.weight_unit) as WeightUnit) || null,
      weight: property.weight || null,
      retailAmount: Number(property.retail_amount),
      serialNumberCaptureMode: property.serial_number_capture_mode as SerialNumberCaptureMode,
      serialNumberPatterns: property.serial_number_patterns
    };
  });
};

export const mapToItemBulkUpsertPayload = (
  shipperCompanyId: number,
  inventoryInfo: InventoryInfo,
  skuDetailProperties: SkuDetailProperties
): BulkItemUpsertRequest => {
  return {
    shipperCompanyId,
    itemsToUpsert: [
      {
        sku: inventoryInfo.sku,
        description: inventoryInfo.description,
        alternateId: inventoryInfo.alternate_id,
        internationalShippingInfo: {
          hsCode: inventoryInfo.hs_code,
          countryOfOrigin: inventoryInfo.country_of_origin
        },
        isHazmat: skuDetailProperties.isHazmat,
        limitedQuantity: skuDetailProperties.limitedQuantity,
        unNumber: skuDetailProperties.unNumber,
        pkgCode: skuDetailProperties.pkgCode,
        lotCodeTrackingEnabled: skuDetailProperties.lotTrackingEnabled,
        expiryDateTrackingEnabled: skuDetailProperties.expiryDateTrackingEnabled,
        inventoryGroup: inventoryInfo.inventory_group,
        csa: new Map(Object.entries(inventoryInfo.csa || {})),
        serialNumberProgram: inventoryInfo.serial_number_program === '' ? null : inventoryInfo.serial_number_program,
        stackable: inventoryInfo.stackable,
        bagAndTapeRequired: skuDetailProperties.bagAndTapeRequired,
        reviewDate: inventoryInfo.review_date === 'Needs review' ? null : inventoryInfo.review_date,
        shelfLifeThreshold: inventoryInfo.shelf_life_threshold,
        properties: mapToItemPropertyToUpsert(inventoryInfo)
      }
    ]
  };
};
